import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  Lock, 
  LogOut, 
  Check, 
  Mail, 
  Trash2, 
  Download, 
  ExternalLink, 
  RefreshCw, 
  Clock, 
  Inbox, 
  Shield, 
  UserCheck,
  Plus,
  Pencil,
  X,
  Save
} from "lucide-react";
import { CrestLogo } from "../components/CrestLogo";
import { HOME } from "../constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("waitlist"); // waitlist, nominations, email-logs
  const [waitlist, setWaitlist] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [dinners, setDinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const emptyDinner = { theme: "", description: "", date_label: "Date TBC", location: "Location TBC", cohort_label: "Future Theme", badge: "", order: 0 };
  const [dinnerForm, setDinnerForm] = useState(null); // null = closed; object = editing/creating
  const [savingDinner, setSavingDinner] = useState(false);
  
  const navigate = useNavigate();

  // Validate Admin User
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data.role !== "admin") {
          toast.error("Unauthorized Access denied.");
          navigate("/admin/login");
        } else {
          setCurrentUser(response.data);
          fetchData();
        }
      } catch (err) {
        console.error(err);
        navigate("/admin/login");
      }
    };
    checkAdmin();
  }, [navigate]);

  // Fetch all dashboard data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [waitlistRes, nominationsRes, logsRes, dinnersRes] = await Promise.all([
        axios.get(`${API}/waitlist`, { withCredentials: true }),
        axios.get(`${API}/nominate`, { withCredentials: true }),
        axios.get(`${API}/email-logs`, { withCredentials: true }),
        axios.get(`${API}/dinners`, { withCredentials: true }),
      ]);
      setWaitlist(waitlistRes.data);
      setNominations(nominationsRes.data);
      setEmailLogs(logsRes.data);
      setDinners(dinnersRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load records.");
    } finally {
      setLoading(false);
    }
  };

  // Status Modifiers
  const updateStatus = async (type, id, newStatus) => {
    try {
      const endpoint = type === "waitlist" ? `/waitlist/${id}` : `/nominate/${id}`;
      await axios.put(`${API}${endpoint}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Entry marked as ${newStatus}`, {
        description: newStatus === "Invited" ? "An invitation email has been sent to the candidate." : null
      });
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status.");
    }
  };

  // Delete records
  const deleteRecord = async (type, id) => {
    if (!window.confirm("Are you sure you wish to delete this entry?")) return;
    try {
      const endpoint = type === "waitlist" ? `/waitlist/${id}` : `/nominate/${id}`;
      await axios.delete(`${API}${endpoint}`, { withCredentials: true });
      toast.success("Record deleted successfully.");
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete record.");
    }
  };

  // Logout Handler
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem("adminUser");
      toast.success("Logged out successfully.");
      navigate("/");
    } catch (err) {
      console.error(err);
      navigate("/");
    }
  };

  // CSV Exporter
  const exportToCSV = (type) => {
    let headers = [];
    let rows = [];
    let filename = "";

    if (type === "waitlist") {
      headers = ["First Name", "Last Name", "Email", "Organisation", "Role", "Sector/Industry", "LinkedIn Profile", "Why Join", "Status", "Date Applied"];
      rows = waitlist.map(w => [
        w.first_name,
        w.last_name,
        w.email,
        w.organisation,
        w.role,
        w.sector || "",
        w.linkedin,
        (w.why_join || "").replace(/"/g, '""'),
        w.status,
        new Date(w.created_at).toLocaleDateString()
      ]);
      filename = "The_Catalyst_Table_Waitlist.csv";
    } else if (type === "nominations") {
      headers = ["Nominator Name", "Nominator Email", "Nominee Name", "Organisation", "Reason for Nomination", "Status", "Date Nominated"];
      rows = nominations.map(n => [
        n.nominator_name,
        n.nominator_email,
        n.nominee_name,
        n.organisation,
        n.why_nominate.replace(/"/g, '""'),
        n.status,
        new Date(n.created_at).toLocaleDateString()
      ]);
      filename = "The_Catalyst_Table_Nominations.csv";
    }

    // Generate CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${val}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV Downloaded Successfully");
  };

  // Dinner management
  const saveDinner = async () => {
    if (!dinnerForm.theme.trim() || !dinnerForm.description.trim()) {
      toast.error("Theme and description are required.");
      return;
    }
    setSavingDinner(true);
    try {
      const payload = {
        theme: dinnerForm.theme,
        description: dinnerForm.description,
        date_label: dinnerForm.date_label || "Date TBC",
        location: dinnerForm.location || "Location TBC",
        cohort_label: dinnerForm.cohort_label || "",
        badge: dinnerForm.badge || "",
        order: Number(dinnerForm.order) || 0,
      };
      if (dinnerForm.id) {
        await axios.put(`${API}/dinners/${dinnerForm.id}`, payload, { withCredentials: true });
        toast.success("Dinner updated.");
      } else {
        await axios.post(`${API}/dinners`, payload, { withCredentials: true });
        toast.success("Dinner added.");
      }
      setDinnerForm(null);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save dinner.");
    } finally {
      setSavingDinner(false);
    }
  };

  const deleteDinner = async (id) => {
    if (!window.confirm("Delete this dinner?")) return;
    try {
      await axios.delete(`${API}/dinners/${id}`, { withCredentials: true });
      toast.success("Dinner deleted.");
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete dinner.");
    }
  };

  return (
    <div className="bg-[#F7F5F2] min-h-screen flex flex-col selection:bg-[#1F3A33] selection:text-[#F7F5F2]" data-testid={HOME.dashboardContainer}>
      <Toaster position="top-right" />

      {/* Admin Header */}
      <header className="bg-[#1F3A33] text-[#F7F5F2] h-20 flex items-center justify-between border-b border-[#C8A97E]/30 px-6 md:px-12">
        <div className="flex items-center gap-3">
          <CrestLogo variant="light" className="w-10 h-10" />
          <span className="font-serif tracking-[0.15em] text-base md:text-lg font-light text-[#F7F5F2] uppercase">
            Committee Control
          </span>
          <span className="hidden md:inline bg-[#C8A97E]/20 text-[#C8A97E] text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border border-[#C8A97E]/20">
            Secure Session
          </span>
        </div>

        <div className="flex items-center gap-6">
          <span className="hidden sm:inline text-xs text-[#F7F5F2]/70 font-sans">
            Signed in: <span className="text-[#C8A97E]">{currentUser?.email}</span>
          </span>
          <button 
            onClick={handleLogout} 
            data-testid={HOME.logoutButton}
            className="flex items-center gap-2 bg-transparent hover:text-[#C8A97E] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-2 px-4 transition-all"
            title="Secure Logout"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Control Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-10 flex-grow flex flex-col space-y-8">
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-[#1F3A33]/15 pb-6 gap-4">
          <div className="text-left space-y-1">
            <h1 className="font-serif text-3xl md:text-4xl font-light text-[#1F3A33]">
              Submission Vault
            </h1>
            <p className="text-xs text-[#8B6A4E] uppercase tracking-widest font-semibold">
              Manage waiting list cohort and direct member nominations
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="flex items-center justify-center gap-2 p-3 border border-[#1F3A33]/15 hover:bg-[#1F3A33]/5 text-[#1F3A33] transition-colors"
              title="Reload Data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            {activeTab === "waitlist" && (
              <button
                onClick={() => exportToCSV("waitlist")}
                data-testid={HOME.dashboardExportWaitlist}
                className="flex items-center gap-2 bg-[#1F3A33] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-3 px-6 hover:bg-[#2E2E2E] transition-all"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}

            {activeTab === "nominations" && (
              <button
                onClick={() => exportToCSV("nominations")}
                data-testid={HOME.dashboardExportNominations}
                className="flex items-center gap-2 bg-[#1F3A33] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-3 px-6 hover:bg-[#2E2E2E] transition-all"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            )}

            {activeTab === "dinners" && (
              <button
                onClick={() => setDinnerForm({ ...emptyDinner, order: dinners.length + 1 })}
                data-testid={HOME.dashboardAddDinner}
                className="flex items-center gap-2 bg-[#1F3A33] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-3 px-6 hover:bg-[#2E2E2E] transition-all"
              >
                <Plus className="w-4 h-4" /> Add Dinner
              </button>
            )}
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-[#1F3A33]/10 gap-6 text-sm font-semibold tracking-widest uppercase">
          <button 
            onClick={() => setActiveTab("waitlist")}
            className={`pb-4 transition-all duration-300 ${activeTab === "waitlist" ? "border-b-2 border-b-[#1F3A33] text-[#1F3A33]" : "text-[#1F3A33]/40 hover:text-[#1F3A33]"}`}
          >
            Waiting List ({waitlist.length})
          </button>
          <button 
            onClick={() => setActiveTab("nominations")}
            className={`pb-4 transition-all duration-300 ${activeTab === "nominations" ? "border-b-2 border-b-[#1F3A33] text-[#1F3A33]" : "text-[#1F3A33]/40 hover:text-[#1F3A33]"}`}
          >
            Nominations ({nominations.length})
          </button>
          <button 
            onClick={() => setActiveTab("dinners")}
            data-testid={HOME.dashboardDinnersTab}
            className={`pb-4 transition-all duration-300 ${activeTab === "dinners" ? "border-b-2 border-b-[#1F3A33] text-[#1F3A33]" : "text-[#1F3A33]/40 hover:text-[#1F3A33]"}`}
          >
            Dinners ({dinners.length})
          </button>
          <button 
            onClick={() => setActiveTab("email-logs")}
            className={`pb-4 transition-all duration-300 ${activeTab === "email-logs" ? "border-b-2 border-b-[#1F3A33] text-[#1F3A33]" : "text-[#1F3A33]/40 hover:text-[#1F3A33]"}`}
          >
            Sent Emails ({emailLogs.length})
          </button>
        </div>

        {/* Data Display */}
        {loading ? (
          <div className="py-24 text-center text-sm font-sans tracking-widest text-[#8B6A4E] uppercase">
            Fetching registries from secure database...
          </div>
        ) : (
          <div className="flex-grow pb-16">
            
            {/* WAITLIST TAB */}
            {activeTab === "waitlist" && (
              <div className="border border-[#1F3A33]/15 bg-white overflow-hidden shadow-none">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-sans text-sm" data-testid={HOME.dashboardWaitlistTable}>
                    <thead>
                      <tr className="bg-[#1F3A33]/5 border-b border-[#1F3A33]/10 text-xs font-semibold uppercase tracking-widest text-[#8B6A4E]">
                        <th className="py-4 px-6">Candidate</th>
                        <th className="py-4 px-6">Professional Context</th>
                        <th className="py-4 px-6">LinkedIn</th>
                        <th className="py-4 px-6">Statement of Interest</th>
                        <th className="py-4 px-6">Status</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F3A33]/10">
                      {waitlist.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="py-12 text-center text-[#2E2E2E]/60 tracking-wider">
                            No active candidates in waiting list.
                          </td>
                        </tr>
                      ) : (
                        waitlist.map((candidate) => (
                          <tr key={candidate._id || candidate.id} className="hover:bg-[#1F3A33]/5 transition-colors">
                            <td className="py-5 px-6">
                              <div className="font-semibold text-[#1F3A33]">
                                {candidate.first_name} {candidate.last_name}
                              </div>
                              <div className="text-xs text-[#2E2E2E]/60">{candidate.email}</div>
                              <div className="text-[10px] text-[#8B6A4E] mt-1 font-mono uppercase">
                                Applied: {new Date(candidate.created_at).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="py-5 px-6">
                              <div className="font-medium text-[#1F3A33]">{candidate.role}</div>
                              <div className="text-xs text-[#2E2E2E]/70">{candidate.organisation}</div>
                            </td>
                            <td className="py-5 px-6">
                              <a 
                                href={candidate.linkedin} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#C8A97E] hover:text-[#1F3A33] flex items-center gap-1 transition-colors text-xs"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                            <td className="py-5 px-6 max-w-xs">
                              <p className="text-xs text-[#2E2E2E]/80 line-clamp-2 hover:line-clamp-none transition-all cursor-help" title={candidate.why_join}>
                                {candidate.why_join}
                              </p>
                            </td>
                            <td className="py-5 px-6">
                              <span className={`inline-block px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase border ${
                                candidate.status === "Approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                candidate.status === "Invited" ? "bg-amber-50 text-amber-800 border-amber-200" :
                                candidate.status === "Rejected" ? "bg-red-50 text-red-800 border-red-200" :
                                "bg-blue-50 text-blue-800 border-blue-200"
                              }`}>
                                {candidate.status}
                              </span>
                            </td>
                            <td className="py-5 px-6 text-right space-x-1 whitespace-nowrap">
                              {candidate.status !== "Approved" && (
                                <button 
                                  onClick={() => updateStatus("waitlist", candidate._id || candidate.id, "Approved")}
                                  className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-emerald-50 hover:text-emerald-800 transition-colors inline-flex"
                                  title="Approve"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </button>
                              )}
                              {candidate.status !== "Invited" && (
                                <button 
                                  onClick={() => updateStatus("waitlist", candidate._id || candidate.id, "Invited")}
                                  className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-amber-50 hover:text-amber-800 transition-colors inline-flex"
                                  title="Invite Dinner"
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                              <button 
                                onClick={() => deleteRecord("waitlist", candidate._id || candidate.id)}
                                className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-red-50 hover:text-red-800 transition-colors inline-flex"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* NOMINATIONS TAB */}
            {activeTab === "nominations" && (
              <div className="border border-[#1F3A33]/15 bg-white overflow-hidden shadow-none">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-sans text-sm" data-testid={HOME.dashboardNominationsTable}>
                    <thead>
                      <tr className="bg-[#1F3A33]/5 border-b border-[#1F3A33]/10 text-xs font-semibold uppercase tracking-widest text-[#8B6A4E]">
                        <th className="py-4 px-6">Nominator</th>
                        <th className="py-4 px-6">Nominee</th>
                        <th className="py-4 px-6">Nominee Organisation</th>
                        <th className="py-4 px-6">Why Should She Pull Up A Seat?</th>
                        <th className="py-4 px-6">Status</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F3A33]/10">
                      {nominations.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="py-12 text-center text-[#2E2E2E]/60 tracking-wider">
                            No direct member nominations received.
                          </td>
                        </tr>
                      ) : (
                        nominations.map((nom) => (
                          <tr key={nom._id || nom.id} className="hover:bg-[#1F3A33]/5 transition-colors">
                            <td className="py-5 px-6">
                              <div className="font-semibold text-[#1F3A33]">{nom.nominator_name}</div>
                              <div className="text-xs text-[#2E2E2E]/60">{nom.nominator_email}</div>
                              <div className="text-[10px] text-[#8B6A4E] mt-1 font-mono uppercase">
                                Date: {new Date(nom.created_at).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="py-5 px-6">
                              <div className="font-semibold text-[#1F3A33]">{nom.nominee_name}</div>
                            </td>
                            <td className="py-5 px-6">
                              <div className="font-medium text-[#1F3A33]">{nom.organisation}</div>
                            </td>
                            <td className="py-5 px-6 max-w-xs">
                              <p className="text-xs text-[#2E2E2E]/80 line-clamp-2 hover:line-clamp-none transition-all cursor-help" title={nom.why_nominate}>
                                {nom.why_nominate}
                              </p>
                            </td>
                            <td className="py-5 px-6">
                              <span className={`inline-block px-2.5 py-1 text-[10px] font-bold tracking-widest uppercase border ${
                                nom.status === "Approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                nom.status === "Invited" ? "bg-amber-50 text-amber-800 border-amber-200" :
                                nom.status === "Rejected" ? "bg-red-50 text-red-800 border-red-200" :
                                "bg-blue-50 text-blue-800 border-blue-200"
                              }`}>
                                {nom.status}
                              </span>
                            </td>
                            <td className="py-5 px-6 text-right space-x-1 whitespace-nowrap">
                              {nom.status !== "Approved" && (
                                <button 
                                  onClick={() => updateStatus("nominate", nom._id || nom.id, "Approved")}
                                  className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-emerald-50 hover:text-emerald-800 transition-colors inline-flex"
                                  title="Approve"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </button>
                              )}
                              {nom.status !== "Invited" && (
                                <button 
                                  onClick={() => updateStatus("nominate", nom._id || nom.id, "Invited")}
                                  className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-amber-50 hover:text-amber-800 transition-colors inline-flex"
                                  title="Simulate Invite"
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                              <button 
                                onClick={() => deleteRecord("nominate", nom._id || nom.id)}
                                className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-red-50 hover:text-red-800 transition-colors inline-flex"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* EMAIL OUTBOX LOGS */}
            {activeTab === "email-logs" && (
              <div className="border border-[#1F3A33]/15 bg-white overflow-hidden shadow-none">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse font-sans text-sm" data-testid={HOME.dashboardEmailLogsTable}>
                    <thead>
                      <tr className="bg-[#1F3A33]/5 border-b border-[#1F3A33]/10 text-xs font-semibold uppercase tracking-widest text-[#8B6A4E]">
                        <th className="py-4 px-6 w-12"></th>
                        <th className="py-4 px-6">Recipient</th>
                        <th className="py-4 px-6">Subject</th>
                        <th className="py-4 px-6">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1F3A33]/10">
                      {emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="py-12 text-center text-[#2E2E2E]/60 tracking-wider">
                            No outbound emails simulated yet.
                          </td>
                        </tr>
                      ) : (
                        emailLogs.map((log) => {
                          const isExpanded = expandedEmailId === (log._id || log.id);
                          return (
                            <React.Fragment key={log._id || log.id}>
                              <tr 
                                className="hover:bg-[#1F3A33]/5 transition-colors cursor-pointer"
                                onClick={() => setExpandedEmailId(isExpanded ? null : (log._id || log.id))}
                              >
                                <td className="py-5 px-6 text-center text-[#C8A97E]">
                                  <Inbox className="w-4 h-4 inline" />
                                </td>
                                <td className="py-5 px-6 font-semibold text-[#1F3A33]">
                                  {log.to_email}
                                </td>
                                <td className="py-5 px-6 text-[#2E2E2E]/80">
                                  {log.subject}
                                </td>
                                <td className="py-5 px-6 text-xs text-[#8B6A4E]">
                                  {new Date(log.sent_at).toLocaleString()}
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-[#1F3A33]/5">
                                  <td colSpan="4" className="py-6 px-12">
                                    <div className="border border-[#1F3A33]/15 bg-[#F7F5F2] p-6 max-w-2xl text-left space-y-4">
                                      <div className="border-b border-[#1F3A33]/10 pb-3 text-xs space-y-1">
                                        <div className="text-[#8B6A4E] uppercase tracking-wider font-semibold">Email Transaction Log</div>
                                        <div><b>From:</b> hello@thecatalysttable.com</div>
                                        <div><b>To:</b> {log.to_email}</div>
                                        <div><b>Subject:</b> {log.subject}</div>
                                        <div><b>Status:</b> {log.status === "failed" ? "Failed to send" : "Sent"}</div>
                                      </div>
                                      <div 
                                        className="font-sans font-light text-sm space-y-3 pt-2 text-[#2E2E2E]"
                                        dangerouslySetInnerHTML={{ __html: log.body }}
                                      ></div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* DINNERS TAB */}
            {activeTab === "dinners" && (
              <div className="space-y-8" data-testid="dashboard-dinners-panel">
                {dinnerForm && (
                  <div className="border border-[#C8A97E]/40 bg-white p-6 md:p-8 space-y-6">
                    <div className="flex items-center justify-between border-b border-[#1F3A33]/10 pb-4">
                      <h3 className="font-serif text-2xl font-light text-[#1F3A33]">
                        {dinnerForm.id ? "Edit Dinner" : "Add New Dinner"}
                      </h3>
                      <button onClick={() => setDinnerForm(null)} className="text-[#1F3A33]/50 hover:text-[#1F3A33]" title="Close">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Theme Title</label>
                        <input
                          type="text"
                          value={dinnerForm.theme}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, theme: e.target.value })}
                          data-testid="dinner-form-theme"
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Cohort / Eyebrow Label</label>
                        <input
                          type="text"
                          value={dinnerForm.cohort_label}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, cohort_label: e.target.value })}
                          placeholder="e.g. Cohort I / Future Theme"
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Description</label>
                      <textarea
                        rows="3"
                        value={dinnerForm.description}
                        onChange={(e) => setDinnerForm({ ...dinnerForm, description: e.target.value })}
                        data-testid="dinner-form-description"
                        className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none resize-none"
                      ></textarea>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Date Label</label>
                        <input
                          type="text"
                          value={dinnerForm.date_label}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, date_label: e.target.value })}
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Location</label>
                        <input
                          type="text"
                          value={dinnerForm.location}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, location: e.target.value })}
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Badge (optional)</label>
                        <input
                          type="text"
                          value={dinnerForm.badge}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, badge: e.target.value })}
                          placeholder="e.g. Founding Dinner"
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-widest text-[#8B6A4E] font-semibold block">Order</label>
                        <input
                          type="number"
                          value={dinnerForm.order}
                          onChange={(e) => setDinnerForm({ ...dinnerForm, order: e.target.value })}
                          className="w-full border border-[#1F3A33]/20 px-3 py-2 text-sm text-[#1F3A33] focus:border-[#C8A97E] outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={saveDinner}
                        disabled={savingDinner}
                        data-testid={HOME.dashboardDinnerSave}
                        className="flex items-center gap-2 bg-[#1F3A33] text-[#F7F5F2] text-xs font-semibold tracking-widest uppercase py-3 px-6 hover:bg-[#2E2E2E] transition-all disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" /> {savingDinner ? "Saving..." : "Save Dinner"}
                      </button>
                      <button
                        onClick={() => setDinnerForm(null)}
                        className="text-xs font-semibold tracking-widest uppercase py-3 px-6 border border-[#1F3A33]/20 text-[#1F3A33] hover:bg-[#1F3A33]/5 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-dinners-list">
                  {dinners.length === 0 ? (
                    <p className="col-span-full text-center text-sm text-[#2E2E2E]/60 py-12">No dinners yet. Add your first dinner.</p>
                  ) : dinners.map((d) => (
                    <div key={d._id || d.id} className="border border-[#1F3A33]/15 bg-white p-6 flex flex-col justify-between relative">
                      {d.badge ? (
                        <span className="absolute top-0 right-0 bg-[#C8A97E] text-[#1F3A33] text-[10px] font-bold tracking-widest uppercase px-2 py-1">{d.badge}</span>
                      ) : null}
                      <div className="space-y-3">
                        {d.cohort_label ? <span className="text-[10px] font-semibold text-[#8B6A4E] uppercase tracking-widest block">{d.cohort_label}</span> : null}
                        <h4 className="font-serif text-xl font-light text-[#1F3A33]">{d.theme}</h4>
                        <p className="text-xs text-[#2E2E2E]/70 leading-relaxed">{d.description}</p>
                      </div>
                      <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#1F3A33]/10">
                        <div className="text-[10px] text-[#8B6A4E] uppercase tracking-wider">
                          {d.date_label} · {d.location}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setDinnerForm({ id: d._id || d.id, theme: d.theme, description: d.description, date_label: d.date_label, location: d.location, cohort_label: d.cohort_label, badge: d.badge, order: d.order })}
                            className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-[#1F3A33]/5 transition-colors"
                            title="Edit"
                            data-testid={`dinner-edit-${d._id || d.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteDinner(d._id || d.id)}
                            className="p-1.5 border border-[#1F3A33]/15 text-[#1F3A33] hover:bg-red-50 hover:text-red-800 transition-colors"
                            title="Delete"
                            data-testid={`dinner-delete-${d._id || d.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

      </div>

      {/* Footer */}
      <footer className="bg-[#1F3A33] text-[#F7F5F2]/50 py-6 border-t border-[#C8A97E]/20 text-center text-xs mt-auto">
        <p>© {new Date().getFullYear()} The Catalyst Table. Secure Admin Console.</p>
      </footer>
    </div>
  );
}
